import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { paths, type Agreement, type DocSend } from "@clevis/shared-types";
import { Button, Input, cn } from "@clevis/shared-ui";
import { db, storage } from "../lib/firebase";
import { call } from "../lib/callFunction";

// One earlier send, opened: each document as it went (the exact file version,
// or the filled-in template as a PDF), whether the ones to sign are signed yet
// (then the PDF is the signed copy), and "Send again".

const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const download = (base64: string, filename: string) => {
  const a = document.createElement("a");
  a.href = `data:application/pdf;base64,${base64}`;
  a.download = filename;
  a.click();
};

export function DocSendDetails({ tenantId, send }: { tenantId: string; send: DocSend & { id: string } }) {
  const [agreements, setAgreements] = useState<Record<string, Agreement>>({});
  const [to, setTo] = useState(send.to.join(", "));
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // The ones to sign: live, so "Signed" shows up the moment they sign.
  useEffect(() => {
    const ids = (send.templates ?? []).map((t) => t.agreementId).filter((x): x is string => !!x);
    const subs = ids.map((id) =>
      onSnapshot(doc(db, paths.tenantAgreement(tenantId, id)), (s) => s.exists() && setAgreements((a) => ({ ...a, [id]: s.data() as Agreement }))),
    );
    return () => subs.forEach((u) => u());
  }, [tenantId, send]);

  async function act(key: string, fn: () => Promise<string | void>) {
    setBusy(key);
    setMsg(null);
    try {
      const text = await fn();
      if (text) setMsg({ ok: true, text });
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mb-2 mt-1 space-y-1.5 rounded-lg border border-steel/20 bg-white p-2">
      {send.docs.map((d) => (
        <div key={`${d.docId}:${d.version}`} className="flex items-center gap-2 text-xs">
          <span className="min-w-0 flex-1 truncate text-ink">
            {d.name}
            <span className="text-steel"> · the version sent ({d.fileName})</span>
          </span>
          <button
            className="text-steel underline hover:text-ink"
            onClick={() => void act(`d${d.docId}`, async () => void window.open(await getDownloadURL(storageRef(storage, d.storagePath)), "_blank", "noopener"))}
          >
            Open
          </button>
        </div>
      ))}
      {(send.templates ?? []).map((t, i) => {
        const ag = t.agreementId ? agreements[t.agreementId] : undefined;
        const status = !t.sign ? "Attached as a PDF" : !ag ? "…" : ag.status === "signed" ? `Signed by ${ag.signerName ?? "the customer"} on ${day(ag.signedAt!)}` : ag.status === "void" ? "Withdrawn" : "Waiting for their signature";
        return (
          <div key={`${t.templateId}:${i}`} className="flex flex-wrap items-center gap-2 text-xs">
            <span className="min-w-0 flex-1 truncate text-ink">
              {t.name}
              <span className={cn(ag?.status === "signed" ? "text-success" : ag?.status === "pending" ? "text-brandorange-dark" : "text-steel")}> · {status}</span>
            </span>
            <button
              className="text-steel underline hover:text-ink"
              disabled={busy !== null}
              onClick={() =>
                void act(`p${i}`, async () => {
                  const r = t.agreementId
                    ? await call<{ base64: string; filename: string }>("getAgreementPdf", { tenantId, agreementId: t.agreementId }, { mutation: false })
                    : await call<{ base64: string; filename: string }>("getDocSendPdf", { tenantId, sendId: send.id, index: i }, { mutation: false });
                  download(r.base64, r.filename);
                })
              }
            >
              {busy === `p${i}` ? "…" : ag?.status === "signed" ? "Signed PDF" : "PDF"}
            </button>
            {ag?.status === "pending" ? (
              <button
                className="text-steel hover:text-danger"
                disabled={busy !== null}
                onClick={() =>
                  void act(`v${i}`, async () => {
                    await call("voidAgreement", { tenantId, agreementId: t.agreementId });
                    return `${t.name} withdrawn: its signing link no longer works.`;
                  })
                }
              >
                Withdraw
              </button>
            ) : null}
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-2 border-t border-steel/10 pt-1.5">
        <Input className="min-w-48 flex-1 py-1 text-xs" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Send again to" />
        <Button
          variant="ghost"
          className="px-2.5 py-1 text-xs"
          busy={busy === "resend"}
          disabled={busy !== null || !to.trim()}
          onClick={() =>
            void act("resend", async () => {
              const list = to.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
              await call("resendDocSend", { tenantId, sendId: send.id, to: list, ccMe: true }, { timeoutMs: 120_000 });
              return `Sent again to ${list.join(", ")}. Signed ones go as the signed copy.`;
            })
          }
        >
          Send again
        </Button>
      </div>
      {msg ? <p className={cn("text-xs", msg.ok ? "text-success" : "text-danger")}>{msg.text}</p> : null}
      <p className="text-[11px] text-steel">Every send stays in this list for your records.</p>
    </div>
  );
}
